import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocsFromServer,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import {
  BUDGET_SCHEMA_VERSION,
  FINANCIAL_CATEGORIES,
  isValidBudgetCategory,
  isValidBudgetLimitCents,
  normalizeCategoryLimits,
  toFirestoreYearMonth,
} from '../domain/finance';
import { awaitRemoteConfirmation, requireConnectionForMutation } from '../services/connectivityService';

const getBudgetRef = (accountId, yearMonth) =>
  doc(firestore, 'accounts', accountId, 'budgets', yearMonth);
const getCustomLimitsRef = (accountId, yearMonth) =>
  collection(firestore, 'accounts', accountId, 'budgets', yearMonth, 'categoryLimits');

const normalizeBudget = (yearMonth, data) => ({
  id: yearMonth,
  yearMonth,
  monthlyLimitCents: isValidBudgetLimitCents(data?.monthlyLimitCents) ? data.monthlyLimitCents : 0,
  categoryLimits: normalizeCategoryLimits(data?.categoryLimits, FINANCIAL_CATEGORIES),
  createdAt: data?.createdAt || null,
  updatedAt: data?.updatedAt || null,
  schemaVersion: data?.schemaVersion || BUDGET_SCHEMA_VERSION,
});

export function useBudget(accountId, selectedPeriod, categories = []) {
  const [queryState, setQueryState] = useState({
    baseBudget: null,
    customLimits: [],
    resolvedRequestKey: null,
    resolvedPeriod: null,
    error: null,
  });
  const queryGenerationRef = useRef(0);
  const yearMonth = toFirestoreYearMonth(selectedPeriod);
  const hasValidRequest = Boolean(accountId && yearMonth);
  const requestKey = JSON.stringify([accountId || '', yearMonth || '']);
  const loading = hasValidRequest && queryState.resolvedRequestKey !== requestKey && !queryState.error;
  const { baseBudget, customLimits, error, resolvedPeriod } = queryState;
  const validCategoryNames = useMemo(
    () => [...FINANCIAL_CATEGORIES, ...categories.filter((item) => item.active !== false).map((item) => item.name)],
    [categories]
  );
  const customByName = useMemo(
    () => new Map(categories.filter((item) => item.source === 'custom').map((item) => [item.name, item])),
    [categories]
  );
  const customById = useMemo(
    () => new Map(categories.filter((item) => item.source === 'custom').map((item) => [item.id, item])),
    [categories]
  );

  useEffect(() => {
    const queryGeneration = queryGenerationRef.current + 1;
    queryGenerationRef.current = queryGeneration;
    let active = true;
    let failed = false;
    let baseResolved = false;
    let customResolved = false;
    let baseServerResolved = false;
    let customServerResolved = false;
    let nextBaseBudget = null;
    let nextCustomLimits = [];
    const unsubscribes = [];

    const isCurrentRequest = () => active && queryGenerationRef.current === queryGeneration;
    const publishIfComplete = () => {
      if (!isCurrentRequest() || failed || !baseResolved || !customResolved) return;
      setQueryState({
        baseBudget: nextBaseBudget,
        customLimits: nextCustomLimits,
        resolvedRequestKey: requestKey,
        resolvedPeriod: selectedPeriod,
        error: null,
      });
    };
    const failRequest = (snapshotError) => {
      if (!isCurrentRequest() || failed) return;
      failed = true;
      setQueryState((current) => ({
        ...current,
        resolvedRequestKey: requestKey,
        resolvedPeriod: selectedPeriod,
        error: snapshotError,
      }));
    };

    if (!hasValidRequest) {
      setQueryState({
        baseBudget: null,
        customLimits: [],
        resolvedRequestKey: requestKey,
        resolvedPeriod: null,
        error: null,
      });
      return () => {
        active = false;
        if (queryGenerationRef.current === queryGeneration) queryGenerationRef.current += 1;
      };
    }

    setQueryState((current) => ({ ...current, error: null }));
    try {
      unsubscribes.push(onSnapshot(
        getBudgetRef(accountId, yearMonth),
        { includeMetadataChanges: true },
        (snapshot) => {
          if (!isCurrentRequest()) return;
          if (snapshot.metadata?.fromCache && !baseServerResolved) {
            setQueryState((current) => ({ ...current, resolvedRequestKey: null, resolvedPeriod: selectedPeriod, error: new Error('BUDGET_DATA_UNAVAILABLE_OFFLINE') }));
            return;
          }
          if (!snapshot.metadata?.fromCache) baseServerResolved = true;
          nextBaseBudget = snapshot.exists() ? normalizeBudget(yearMonth, snapshot.data()) : null;
          baseResolved = true;
          publishIfComplete();
        },
        failRequest
      ));
      unsubscribes.push(onSnapshot(
        getCustomLimitsRef(accountId, yearMonth),
        { includeMetadataChanges: true },
        (snapshot) => {
          if (!isCurrentRequest()) return;
          if (snapshot.metadata?.fromCache && !customServerResolved) {
            setQueryState((current) => ({ ...current, resolvedRequestKey: null, resolvedPeriod: selectedPeriod, error: new Error('BUDGET_DATA_UNAVAILABLE_OFFLINE') }));
            return;
          }
          if (!snapshot.metadata?.fromCache) customServerResolved = true;
          nextCustomLimits = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
          customResolved = true;
          publishIfComplete();
        },
        failRequest
      ));
    } catch (snapshotError) {
      failRequest(snapshotError);
    }

    return () => {
      active = false;
      if (queryGenerationRef.current === queryGeneration) queryGenerationRef.current += 1;
      unsubscribes.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
    };
  }, [accountId, hasValidRequest, requestKey, selectedPeriod, yearMonth]);

  const budget = useMemo(() => {
    if (!baseBudget) return null;
    const customCategoryLimits = Object.fromEntries(customLimits.flatMap((limit) => {
      const category = customById.get(limit.categoryId);
      if (!category || category.active === false) return [];
      return [[category.name, limit.limitCents]];
    }));
    return { ...baseBudget, categoryLimits: { ...baseBudget.categoryLimits, ...customCategoryLimits } };
  }, [baseBudget, customById, customLimits]);

  const mutateBudget = useCallback(async (mutation) => {
    if (!accountId || !yearMonth) throw new Error('BUDGET_ACCOUNT_REQUIRED');
    requireConnectionForMutation();
    const budgetRef = getBudgetRef(accountId, yearMonth);
    return awaitRemoteConfirmation(runTransaction(firestore, async (transaction) => {
      const snapshot = await transaction.get(budgetRef);
      const current = snapshot.exists() ? normalizeBudget(yearMonth, snapshot.data()) : normalizeBudget(yearMonth, {});
      const next = mutation(current);
      const payload = {
        yearMonth,
        monthlyLimitCents: next.monthlyLimitCents,
        categoryLimits: normalizeCategoryLimits(next.categoryLimits, FINANCIAL_CATEGORIES),
        schemaVersion: BUDGET_SCHEMA_VERSION,
        createdAt: snapshot.exists() ? snapshot.data().createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      transaction.set(budgetRef, payload);
      return payload;
    }));
  }, [accountId, yearMonth]);

  const saveMonthlyLimit = useCallback(async (monthlyLimitCents) => {
    if (!isValidBudgetLimitCents(monthlyLimitCents)) throw new Error('INVALID_BUDGET_LIMIT');
    return mutateBudget((current) => ({ ...current, monthlyLimitCents }));
  }, [mutateBudget]);

  const saveCategoryLimit = useCallback(async (category, limitCents) => {
    if (!isValidBudgetCategory(category, validCategoryNames)) throw new Error('INVALID_BUDGET_CATEGORY');
    if (!isValidBudgetLimitCents(limitCents)) throw new Error('INVALID_BUDGET_LIMIT');
    const customCategory = customByName.get(category);
    if (customCategory) {
      requireConnectionForMutation();
      const limitRef = doc(getCustomLimitsRef(accountId, yearMonth), customCategory.id);
      await awaitRemoteConfirmation(runTransaction(firestore, async (transaction) => {
        const snapshot = await transaction.get(limitRef);
        transaction.set(limitRef, {
          categoryId: customCategory.id,
          categoryNameSnapshot: customCategory.name,
          limitCents,
          createdAt: snapshot.exists() ? snapshot.data().createdAt : serverTimestamp(),
          updatedAt: serverTimestamp(),
          schemaVersion: 1,
        });
      }));
      return;
    }
    return mutateBudget((current) => ({
      ...current,
      categoryLimits: { ...current.categoryLimits, [category]: limitCents },
    }));
  }, [accountId, customByName, mutateBudget, validCategoryNames, yearMonth]);

  const removeCategoryLimit = useCallback(async (category) => {
    if (!isValidBudgetCategory(category, validCategoryNames)) throw new Error('INVALID_BUDGET_CATEGORY');
    const customCategory = customByName.get(category);
    if (customCategory) {
      requireConnectionForMutation();
      await awaitRemoteConfirmation(deleteDoc(doc(getCustomLimitsRef(accountId, yearMonth), customCategory.id)));
      return;
    }
    return mutateBudget((current) => {
      const categoryLimits = { ...current.categoryLimits };
      delete categoryLimits[category];
      return { ...current, categoryLimits };
    });
  }, [accountId, customByName, mutateBudget, validCategoryNames, yearMonth]);

  const deleteBudget = useCallback(async () => {
    if (!accountId || !yearMonth) throw new Error('BUDGET_ACCOUNT_REQUIRED');
    requireConnectionForMutation();
    const customSnapshot = await getDocsFromServer(getCustomLimitsRef(accountId, yearMonth));
    const batch = writeBatch(firestore);
    customSnapshot.docs.forEach((item) => batch.delete(item.ref));
    batch.delete(getBudgetRef(accountId, yearMonth));
    await awaitRemoteConfirmation(batch.commit());
  }, [accountId, yearMonth]);

  return {
    budget,
    loading,
    error,
    resolvedPeriod,
    saveMonthlyLimit,
    saveCategoryLimit,
    removeCategoryLimit,
    deleteBudget,
  };
}
